import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import {
  Eye, Heart, ClipboardList, CheckCircle, Loader2,
  ArrowRight, AlertTriangle, TrendingDown, Gift,
} from "lucide-react";

const SORT_OPTIONS = [
  { key: "views", label: "Most Viewed" },
  { key: "conversion", label: "Best Converting" },
  { key: "bottleneck", label: "Biggest Bottlenecks" },
];

const BOTTLENECK_LABELS = {
  "view→fav": "Nobody favorites it",
  "fav→track": "Favorited but not tracked",
  "track→claim": "Tracked but not claimed",
};

const FunnelBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.max((value / max) * 100, 2) : 0;
  return (
    <div className="h-5 bg-slate-100 rounded-full overflow-hidden flex-1">
      <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const FunnelRow = ({ item, maxViews, onNavigate }) => {
  const hasBottleneck = !!item.bottleneck;
  return (
    <div
      className={`rounded-xl border p-3 space-y-2 ${hasBottleneck ? "bg-amber-50/50 border-amber-200" : "bg-white border-slate-100"}`}
      data-testid={`funnel-row-${item.business_id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center">
          <BrandLogo name={item.name} logoUrl={item.logo_url} size="sm" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onNavigate(`/offer/${item.business_id}`)}
            className="text-sm font-semibold text-slate-800 truncate hover:text-sky-600 transition-colors block"
            data-testid={`funnel-link-${item.business_id}`}
          >
            {item.name}
          </button>
          <p className="text-[10px] text-slate-400 truncate">{item.offer_title}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            item.overall_conversion_pct > 5 ? "bg-green-100 text-green-700" :
            item.overall_conversion_pct > 0 ? "bg-amber-100 text-amber-700" :
            "bg-slate-100 text-slate-500"
          }`}>
            {item.overall_conversion_pct}%
          </span>
        </div>
      </div>

      {/* Funnel bars */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Eye className="w-3 h-3 text-slate-400 flex-shrink-0" />
          <FunnelBar value={item.views} max={maxViews} color="bg-sky-400" />
          <span className="text-[10px] text-slate-500 w-8 text-right font-medium">{item.views}</span>
        </div>
        <div className="flex items-center gap-2">
          <Heart className="w-3 h-3 text-pink-400 flex-shrink-0" />
          <FunnelBar value={item.favorites} max={maxViews} color="bg-pink-400" />
          <span className="text-[10px] text-slate-500 w-8 text-right font-medium">{item.favorites}</span>
        </div>
        <div className="flex items-center gap-2">
          <ClipboardList className="w-3 h-3 text-violet-400 flex-shrink-0" />
          <FunnelBar value={item.tracked} max={maxViews} color="bg-violet-400" />
          <span className="text-[10px] text-slate-500 w-8 text-right font-medium">{item.tracked}</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
          <FunnelBar value={item.claimed} max={maxViews} color="bg-emerald-500" />
          <span className="text-[10px] text-slate-500 w-8 text-right font-medium">{item.claimed}</span>
        </div>
      </div>

      {/* Drop-off rates */}
      <div className="flex items-center gap-3 text-[10px] text-slate-400">
        <span>View{"\u2192"}Fav: {item.view_to_fav_pct}%</span>
        <span>Fav{"\u2192"}Track: {item.fav_to_track_pct}%</span>
        <span>Track{"\u2192"}Claim: {item.track_to_claim_pct}%</span>
      </div>

      {/* Bottleneck callout */}
      {hasBottleneck && (
        <div className="flex items-center gap-1.5 bg-amber-100 rounded-lg px-2.5 py-1.5">
          <TrendingDown className="w-3 h-3 text-amber-600 flex-shrink-0" />
          <span className="text-[10px] font-semibold text-amber-700">
            Bottleneck: {BOTTLENECK_LABELS[item.bottleneck] || item.bottleneck}
          </span>
        </div>
      )}
    </div>
  );
};

export default function ConversionFunnelPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("views");

  const fetchData = useCallback(async (sortBy) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/conversion-funnel?sort=${sortBy}`);
      setData(res.data);
    } catch (e) {
      console.error("Funnel error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(sort);
  }, [sort, fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
      </div>
    );
  }

  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Failed to load funnel data</p>;

  const { summary, funnels } = data;
  const maxViews = funnels.length > 0 ? Math.max(...funnels.map((f) => f.views)) : 1;

  return (
    <div className="space-y-4" data-testid="conversion-funnel-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
          <p className="text-lg font-bold text-sky-700">{summary.total_views}</p>
          <p className="text-[10px] text-sky-500 font-medium">Total Views</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-lg font-bold text-emerald-700">{summary.total_claims}</p>
          <p className="text-[10px] text-emerald-500 font-medium">Total Claims</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-lg font-bold text-amber-700">{summary.avg_conversion_pct}%</p>
          <p className="text-[10px] text-amber-500 font-medium">Avg Conversion</p>
        </div>
      </div>

      {/* Bottleneck distribution */}
      {Object.keys(summary.bottleneck_distribution).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700">Where users drop off most</span>
          </div>
          <div className="flex gap-3">
            {Object.entries(summary.bottleneck_distribution).map(([key, count]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-amber-800">{count}</span>
                <span className="text-[10px] text-amber-600">{BOTTLENECK_LABELS[key] || key}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 px-1">
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-sky-400" /><span className="text-[10px] text-slate-500">Views</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-pink-400" /><span className="text-[10px] text-slate-500">Favorites</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-violet-400" /><span className="text-[10px] text-slate-500">Tracked</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[10px] text-slate-500">Claimed</span></div>
      </div>

      {/* Sort */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => setSort(opt.key)}
            className={`flex-1 py-1.5 text-[10px] font-semibold rounded-md transition-all ${
              sort === opt.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
            data-testid={`funnel-sort-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Funnel rows */}
      <div className="space-y-2">
        {funnels.map((item) => (
          <FunnelRow key={item.business_id} item={item} maxViews={maxViews} onNavigate={navigate} />
        ))}
      </div>

      {funnels.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">No offers with activity yet</p>
      )}
    </div>
  );
}
